import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Plus, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, DIFFICULTIES } from "@/lib/api";
import { StepEditor } from "@/pages/admin/StepEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { AiWriteButton } from "@/components/AiWriteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const newStep = () => ({ id: null, type: "content", title: "", body: "", image_url: null, video_url: null, breathing: null, _key: Math.random().toString(36).slice(2) });
const blank = { title: "", summary: "", category_id: "", difficulty: "Beginner", estimated_minutes: 5, cover_image: "", status: "draft", audience: "all", steps: [newStep()] };

export default function TutorialEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(isNew ? blank : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`/tutorials/${id}`).then((r) => {
        const t = r.data;
        setForm({ title: t.title, summary: t.summary, category_id: t.category_id || "", difficulty: t.difficulty, estimated_minutes: t.estimated_minutes, cover_image: t.cover_image || "", status: t.status, audience: t.audience || "all", steps: t.steps.map((s) => ({ ...s, _key: s.id })) });
      }).catch((e) => { toast.error(errorMessage(e)); navigate("/admin"); });
    }
  }, [id, isNew, navigate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateStep = (i, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));
  const moveStep = (i, dir) => setForm((f) => {
    const steps = [...f.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return f;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...f, steps };
  });

  const save = async (status) => {
    if (!form.title.trim()) { toast.error("Please add a title"); return; }
    if (form.steps.some((s) => !s.title.trim())) { toast.error("Every step needs a title"); return; }
    setSaving(true);
    const payload = {
      ...form, status, category_id: form.category_id || null, cover_image: form.cover_image || null,
      estimated_minutes: Number(form.estimated_minutes) || 1,
      steps: form.steps.map(({ _key, ...s }) => ({ ...s, image_url: s.image_url || null, video_url: s.video_url || null, breathing: s.type === "breathing" ? s.breathing : null })),
    };
    try {
      const r = isNew ? await api.post("/admin/tutorials", payload) : await api.put(`/admin/tutorials/${id}`, payload);
      toast.success(status === "published" ? "Tutorial published" : "Draft saved");
      if (isNew) navigate(`/admin/tutorials/${r.data.id}`, { replace: true });
      else set({ status, steps: r.data.steps.map((s) => ({ ...s, _key: s.id })) });
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  if (!form) return <div className="max-w-5xl mx-auto px-4 py-12"><Skeleton className="h-96 rounded-3xl" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="tutorial-editor">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="editor-back-link"><ArrowLeft className="w-4 h-4" /> Back to tutorials</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow">{isNew ? "New tutorial" : "Edit tutorial"}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2">{form.title || "Untitled tutorial"}</h1>
          <p className="text-sm text-slate-500 mt-1">Status: <span className={`font-semibold ${form.status === "published" ? "text-emerald-700" : "text-slate-700"}`} data-testid="editor-status">{form.status}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && <Button variant="ghost" className="rounded-full" asChild><Link to={`/tutorials/${id}`} data-testid="editor-preview-link"><Eye className="w-4 h-4" /> Preview</Link></Button>}
          <Button variant="outline" className="rounded-full" disabled={saving} onClick={() => save("draft")} data-testid="editor-save-draft-button"><Save className="w-4 h-4" /> Save draft</Button>
          <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={saving} onClick={() => save("published")} data-testid="editor-publish-button"><Send className="w-4 h-4" /> Publish</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 rounded-3xl bg-white border border-slate-200 p-6 space-y-5">
          <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => set({ title: e.target.value })} className="rounded-xl" data-testid="editor-title-input" /></div>
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <Label>Summary</Label>
              <AiWriteButton field="summary" value={form.summary} onChange={(v) => set({ summary: v })} context={{ tutorial_title: form.title }} testId="editor-summary-ai" />
            </div>
            <Textarea value={form.summary} onChange={(e) => set({ summary: e.target.value })} rows={3} className="rounded-xl" data-testid="editor-summary-input" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={form.category_id || "none"} onValueChange={(v) => set({ category_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-xl" data-testid="editor-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Uncategorized</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => set({ difficulty: v })}>
                <SelectTrigger className="rounded-xl" data-testid="editor-difficulty-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Minutes</Label><Input type="number" min={1} value={form.estimated_minutes} onChange={(e) => set({ estimated_minutes: e.target.value })} className="rounded-xl" data-testid="editor-minutes-input" /></div>
          </div>
          <div className="space-y-2">
            <Label>Who can see it</Label>
            <Select value={form.audience} onValueChange={(v) => set({ audience: v })}>
              <SelectTrigger className="rounded-xl" data-testid="editor-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Everyone (clients & clinicians)</SelectItem><SelectItem value="clinicians">Clinicians only</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Cover image <span className="text-xs text-slate-400 font-normal">(optional)</span></Label><ImageUpload value={form.cover_image} onChange={(v) => set({ cover_image: v || "" })} testId="editor-cover" height="h-36" /></div>
        </aside>

        <section className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">Steps <span className="text-slate-400 font-medium">({form.steps.length})</span></h2>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => set({ steps: [...form.steps, newStep()] })} data-testid="editor-add-step-button"><Plus className="w-4 h-4" /> Add step</Button>
          </div>
          {form.steps.length === 0 && <p className="rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="editor-no-steps">No steps yet. Add the first one.</p>}
          {form.steps.map((s, i) => (
            <StepEditor key={s._key} step={s} index={i} total={form.steps.length} update={(p) => updateStep(i, p)} remove={() => removeStep(i)} move={(d) => moveStep(i, d)} tutorialTitle={form.title} />
          ))}
          {form.steps.length > 0 && (
            <Button variant="outline" className="rounded-full w-full" onClick={() => set({ steps: [...form.steps, newStep()] })} data-testid="editor-add-step-bottom-button"><Plus className="w-4 h-4" /> Add another step</Button>
          )}
        </section>
      </div>
    </div>
  );
}
